import { useCallback, useEffect, useRef, useState } from "react";
import { Button, EmptyState, Icon, IconButton, Notice, StatusBadge, Total } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { useAbbrechen, useFertig, usePositionEntfernen, useScan, type GeraetDaten, type ScanErgebnis, type WarenkorbAnsicht as WK } from "./api";
import { scanTon, useKameraScanner, useTastaturScanner } from "./scanner";
import { ApiFehler } from "../api/client";
import { formatDatum, formatEuro } from "./format";
import { RabattDialog } from "./Rabatt";
import { OhneEtikett } from "./OhneEtikett";
import { Rueckgabe } from "./Rueckgabe";
import { Ladenware } from "./Ladenware";
import { Gutschein } from "./Gutschein";

const KAMERA_KEY = "rl.kasse.kamera";

export function WarenkorbAnsicht({ daten, warenkorb }: { daten: GeraetDaten; warenkorb: WK }) {
  const scan = useScan();
  const entfernen = usePositionEntfernen();
  const fertig = useFertig();
  const abbrechen = useAbbrechen();
  const [meldung, setMeldung] = useState<{ tone: "attention" | "error" | "info" | "success"; titel: string; text?: string } | null>(null);
  const [modus, setModus] = useState<"scan" | "ohne_etikett" | "rueckgabe" | "ladenware" | "gutschein">("scan");
  const [rabattOffen, setRabattOffen] = useState(false);
  const rechte = daten.kassierer?.rechte ?? [];
  const darfRueckgabe = rechte.includes("rueckgabe");
  const [kameraAn, setKameraAn] = useState(() => {
    try {
      return localStorage.getItem(KAMERA_KEY) !== "aus" && daten.geraet?.scannerArt !== "bluetooth";
    } catch {
      return true;
    }
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const ton = daten.einstellungen?.scanTon ?? true;

  const verarbeite = useCallback(
    async (code: string) => {
      if (scan.isPending) return;
      try {
        const r = await scan.mutateAsync({ id: warenkorb.id, code });
        const e = r.ergebnis as ScanErgebnis | undefined;
        if (!e) return;
        if (e.art === "ok") {
          if (ton) scanTon("ok");
          setMeldung(e.hinweis === "mietzeit_beendet" ? { tone: "info", titel: "Mietzeit dieses Mieters ist beendet", text: "Verkauf ist möglich; der Erlös landet in der Schlussabrechnung." } : null);
          requestAnimationFrame(() => listeRef.current?.scrollTo({ top: listeRef.current.scrollHeight, behavior: "smooth" }));
        } else {
          if (ton) scanTon("warnung");
          if (e.art === "doppelt") setMeldung({ tone: "attention", titel: "Etikett zweimal gescannt", text: `${e.bezeichnung} liegt schon im Warenkorb. Ein zweites Exemplar wurde nicht hinzugefügt.` });
          else if (e.art === "schon_verkauft") setMeldung({ tone: "error", titel: `Schon verkauft${e.verkauftAt ? ` am ${formatDatum(e.verkauftAt)}` : ""}`, text: `${e.bezeichnung} (${e.code}) nicht verkaufen – bitte an den Inhaber geben.` });
          else if (e.art === "in_anderem_warenkorb") setMeldung({ tone: "attention", titel: "Liegt in einem anderen Warenkorb", text: `${e.bezeichnung} wird gerade an einer anderen Kasse kassiert.` });
          else if (e.art === "nicht_im_regal") setMeldung({ tone: "attention", titel: "Artikel ist nicht im Regal", text: `${e.bezeichnung}: Status „${e.status}“. Bitte den Inhaber fragen.` });
          else setMeldung({ tone: "attention", titel: "Unbekannter Code", text: e.code ? `Kein Artikel zu ${e.code}.` : "Das ist kein Etikett dieses Ladens." });
        }
      } catch (err) {
        setMeldung({ tone: "error", titel: "Scan nicht übernommen", text: err instanceof ApiFehler ? err.message : "Bitte noch einmal scannen." });
      }
    },
    [scan, warenkorb.id, ton],
  );

  const imScan = modus === "scan" && !rabattOffen;
  const kameraStatus = useKameraScanner(videoRef, kameraAn && imScan, verarbeite);
  useTastaturScanner(imScan, verarbeite);

  useEffect(() => {
    try {
      localStorage.setItem(KAMERA_KEY, kameraAn ? "an" : "aus");
    } catch {
      // egal
    }
  }, [kameraAn]);

  // Tastaturkürzel: F Fertig, S Suche (ohne Etikett), R Rabatt, Esc Abbrechen (nicht in Eingabefeldern)
  useEffect(() => {
    if (!imScan) return;
    const onKey = (e: KeyboardEvent) => {
      const ziel = e.target as HTMLElement | null;
      if (ziel && (ziel.tagName === "INPUT" || ziel.tagName === "TEXTAREA")) return;
      if (e.key === "s" || e.key === "S") {
        e.preventDefault();
        setModus("ohne_etikett");
      }
      if ((e.key === "r" || e.key === "R") && warenkorb.positionen.some((p) => p.art === "artikel")) {
        e.preventDefault();
        setRabattOffen(true);
      }
      if ((e.key === "f" || e.key === "F") && warenkorb.summeCent > 0 && !fertig.isPending) {
        e.preventDefault();
        fertig.mutate({ id: warenkorb.id });
      }
      if (e.key === "Escape" && warenkorb.positionen.length) {
        e.preventDefault();
        if (confirm("Warenkorb abbrechen? Alle Artikel gehen zurück ins Regal.")) abbrechen.mutate({ id: warenkorb.id, grund: "Abbruch an der Kasse" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [warenkorb, fertig, abbrechen, imScan]);

  if (modus === "ohne_etikett") {
    return (
      <OhneEtikett
        onZurueck={() => setModus("scan")}
        onWahl={async (t) => {
          await verarbeite(t.code);
          setModus("scan");
        }}
      />
    );
  }
  if (modus === "rueckgabe") {
    return (
      <Rueckgabe
        onZurueck={() => setModus("scan")}
        onFertig={(betragCent) => {
          setModus("scan");
          setMeldung({ tone: "success", titel: `Rückgabe gebucht · ${formatEuro(betragCent)} erstattet`, text: "Artikel in die Rückgabe-Kiste legen. Der Mieter wird informiert." });
        }}
      />
    );
  }
  if (modus === "ladenware") return <Ladenware warenkorb={warenkorb} onZurueck={() => setModus("scan")} />;
  if (modus === "gutschein") return <Gutschein daten={daten} warenkorb={warenkorb} onZurueck={() => setModus("scan")} onFertig={(text) => { setModus("scan"); setMeldung({ tone: "success", titel: text }); }} />;

  const leer = warenkorb.positionen.length === 0;
  const kameraText = !kameraAn ? "Kamera aus" : kameraStatus === "aktiv" ? "Kamera aktiv" : kameraStatus === "verweigert" ? "Kamera nicht erlaubt" : kameraStatus === "fehlt" ? "Keine Kamera" : "Kamera startet …";

  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart" aria-label="Warenkorb">
        {warenkorb.uebung ? <div className="kasse-uebung" role="status"><Icon name="info" size={18} /> Übungsmodus – dieser Warenkorb wird nicht verbucht, keine Zahlung wird gesucht.</div> : null}
        <div className="sc-scanstrip">
          <button type="button" className="kasse-video-btn" onClick={() => setKameraAn((v) => !v)} aria-label={kameraAn ? "Kamera ausschalten" : "Kamera einschalten"} style={{ border: 0, padding: 0, background: "transparent", cursor: "pointer" }}>
            {kameraAn ? (
              <video ref={videoRef} className="kasse-video" muted playsInline />
            ) : (
              <span className="kasse-video kasse-video--aus">
                <Icon name="camera" size={28} />
              </span>
            )}
          </button>
          <div className="sc-col sc-grow" style={{ gap: 2 }}>
            <span className="sc-strong">{scan.isPending ? "Wird geprüft …" : "Bereit zum Scannen"}</span>
            <span className="sc-muted sc-small">
              {kameraText} · Bluetooth-Scanner jederzeit
            </span>
          </div>
          <Button variant="secondary" icon="search" onClick={() => setModus("ohne_etikett")}>
            Ohne Etikett
          </Button>
        </div>
        {meldung ? (
          <div style={{ padding: "var(--space-3) var(--space-5) 0" }}>
            <Notice tone={meldung.tone} title={meldung.titel} action={<IconButton icon="x" label="Hinweis schließen" onClick={() => setMeldung(null)} />}>
              {meldung.text}
            </Notice>
          </div>
        ) : null}
        <div className="sc-cartlist" ref={listeRef}>
          {leer ? (
            <EmptyState compact illustration="empty-shelf" title="Noch keine Artikel im Warenkorb">
              Den ersten Artikel scannen – die Summe rechnet sich von selbst.
            </EmptyState>
          ) : (
            warenkorb.positionen.map((p, i) => (
              <ItemRow
                key={p.id}
                title={p.bezeichnung}
                photo={p.fotoKey ? `/api/laden/dateien/${p.fotoKey}` : p.art === "ladenware" ? false : undefined}
                meta={p.art === "ladenware" ? "Ladenware" : [p.regalNummer && `Regal ${p.regalNummer}`, p.code, p.mieterName].filter(Boolean).join(" · ")}
                price={p.endpreisCent}
                status={p.rabattCent ? <StatusBadge status={p.rabattGrund === "mieteraktion" ? "paid" : "neutral"}>{p.rabattGrund === "mieteraktion" ? "Aktion des Mieters" : "Rabatt"} −{formatEuro(p.rabattCent)}</StatusBadge> : p.hinweis === "mietzeit_beendet" ? <StatusBadge status="attention">Mietzeit beendet</StatusBadge> : undefined}
                selected={i === warenkorb.positionen.length - 1 && scan.isSuccess}
                action={<IconButton icon="x" label={`${p.bezeichnung} entfernen`} onClick={() => entfernen.mutate({ id: warenkorb.id, pos: p.id })} disabled={entfernen.isPending} />}
              />
            ))
          )}
        </div>
        <div className="sc-cartfoot">
          <Button variant="secondary" icon="percent" disabled={!warenkorb.positionen.some((p) => p.art === "artikel")} onClick={() => setRabattOffen(true)}>
            Rabatt
          </Button>
          <Button variant="secondary" icon="undo-2" disabled={!darfRueckgabe} title={darfRueckgabe ? undefined : "Rückgaben buchen nur Inhaber und Mitarbeiter"} onClick={() => setModus("rueckgabe")}>
            Rückgabe
          </Button>
          <Button variant="secondary" icon="store" onClick={() => setModus("ladenware")}>
            Ladenware
          </Button>
          <Button variant="secondary" icon="wallet" onClick={() => setModus("gutschein")}>
            Gutschein
          </Button>
          <span className="sc-grow" />
          <Button variant="quiet" icon="trash-2" disabled={leer || abbrechen.isPending} onClick={() => confirm("Warenkorb abbrechen? Alle Artikel gehen zurück ins Regal.") && abbrechen.mutate({ id: warenkorb.id, grund: "Abbruch an der Kasse" })}>
            Warenkorb abbrechen
          </Button>
        </div>
      </section>
      <aside className="sc-kasse__side" aria-label="Summe">
        <Total amountCent={warenkorb.summeCent} caption={leer ? "Noch keine Artikel" : `${warenkorb.positionen.length} ${warenkorb.positionen.length === 1 ? "Artikel" : "Artikel"}${warenkorb.anzahlMieter ? ` von ${warenkorb.anzahlMieter} ${warenkorb.anzahlMieter === 1 ? "Mieter" : "Mietern"}` : ""}`} />
        <span className="sc-grow" />
        <p className="kasse-side__hint">Danach den Betrag in SumUp eintippen. Bar oder Karte – die Zahlung wird automatisch erkannt.</p>
        <Button variant="primary" size="xl" block disabled={leer || warenkorb.summeCent <= 0} loading={fertig.isPending} onClick={() => fertig.mutate({ id: warenkorb.id })}>
          Fertig{!leer ? ` · ${formatEuro(warenkorb.summeCent)}` : ""}
        </Button>
        <div className="kasse-kbd">
          <span className="sc-kbd">F</span> Fertig · <span className="sc-kbd">S</span> Suche · <span className="sc-kbd">R</span> Rabatt · <span className="sc-kbd">Esc</span> Abbrechen
        </div>
      </aside>
      {rabattOffen ? <RabattDialog offen onClose={() => setRabattOffen(false)} warenkorb={warenkorb} daten={daten} /> : null}
    </div>
  );
}
